import { useMemo } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, ArrowRight,
  AlertTriangle, BarChart3, Wallet, Building2, Clock,
  CircleAlert, CircleCheck, CircleMinus, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Line, ComposedChart, ReferenceLine,
} from 'recharts';

/* ---------- types ---------- */
interface EntityHealth {
  entity_id: string;
  entity_name: string;
  group_tag: string;
  min_capital_eur: number;
  opening_balance: number;
  month_revenue: number;
  month_expenses: number;
  projected_balance: number;
  missing_ob: boolean;
  latest_balance_date: string;
  below_min_capital: boolean;
}

interface DataGaps {
  total_entities: number;
  entities_with_ob: number;
  entities_missing_ob: number;
  overdue_items_count: number;
  overdue_items_total: number;
  stale_balance_entities: number;
}

interface KPIData {
  current_week_revenue: number;
  current_week_expenses: number;
  current_week_pl: number;
  current_month_revenue: number;
  current_month_expenses: number;
  current_month_pl: number;
  ytd_revenue: number;
  ytd_expenses: number;
  ytd_pl: number;
  monthly_budget_plan: { revenue: number; expenses: number; net: number };
  monthly_corrected_plan: { revenue: number; expenses: number; net: number };
  plan_vs_fact_variance: {
    budget_revenue_var: number;
    budget_expense_var: number;
    budget_net_var: number;
    corrected_revenue_var: number;
    corrected_expense_var: number;
    corrected_net_var: number;
  };
  balances_by_entity: {
    entity_id: string;
    entity_name: string;
    group_tag: string;
    balance: number;
    currency: string;
    week_start: string;
  }[];
  weekly_trend: {
    week_start: string;
    revenue: number;
    expenses: number;
    net: number;
  }[];
  cashflow_items: {
    planned_expenses_this_month: number;
    completed_expenses_this_month: number;
    upcoming_expenses: number;
    upcoming_expenses_count: number;
  };
  this_week: { revenue: number; expenses: number; net: number };
  last_week: { revenue: number; expenses: number; net: number };
  monthly_opening_balance: number;
  projected_closing_balance: number;
  data_gaps: DataGaps;
  entity_health: EntityHealth[];
}

/* ---------- helpers ---------- */
// Raw number format (no currency symbol) — used for chart axes/tooltips
function fmtRaw(amount: number, compact = false) {
  if (compact && Math.abs(amount) >= 1000) {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(amount);
  }
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}

function plColor(val: number) {
  return val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-500' : 'text-muted-foreground';
}

function varPct(actual: number, plan: number): string | null {
  if (plan === 0) return null;
  const pct = ((actual - plan) / Math.abs(plan)) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

function daysSince(dateStr: string): number {
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

/* ---------- skeleton ---------- */
function DashboardSkeleton() {
  return (
    <div className="p-4 md:p-6 space-y-5">
      <Skeleton className="h-7 w-56 mb-2" />
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent className="p-4"><Skeleton className="h-14 w-full" /></CardContent></Card>
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/* ---------- main component ---------- */
interface FinDashboardProps {
  onNavigate?: (page: string) => void;
}

export default function FinDashboard({ onNavigate }: FinDashboardProps) {
  const { data, loading, error } = useFetch<KPIData>('/api/fin/management-kpis');

  if (loading && !data) return <DashboardSkeleton />;

  if (error && !data) {
    return (
      <div className="p-4 md:p-6 space-y-5">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">Management Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Weekly cash flow management overview</p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <AlertTriangle size={32} className="text-amber-500 mb-3" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => window.location.reload()}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return <DashboardContent data={data} onNavigate={onNavigate} />;
}

/* ---------- dashboard content ---------- */
function DashboardContent({ data, onNavigate }: { data: KPIData; onNavigate?: (page: string) => void }) {
  const nav = (page: string) => onNavigate?.(page);
  const { symbol, fmt: cfmt } = useCurrency();
  // Shorthand: format EUR-denominated API values in the user's base currency
  const f = (amount: number, compact = false) => cfmt(amount, 'EUR', { compact });
  const gaps = data.data_gaps;
  const alertCount = (gaps?.overdue_items_count || 0)
    + (gaps?.entities_missing_ob || 0)
    + (gaps?.stale_balance_entities || 0)
    + (data.entity_health?.filter(e => e.below_min_capital).length || 0);

  // Chart data — last 12 weeks with net line
  const chartData = useMemo(() => {
    return data.weekly_trend.map(w => ({
      week: new Date(w.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      Revenue: w.revenue,
      Expenses: Math.abs(w.expenses),
      Net: w.net,
    }));
  }, [data.weekly_trend]);

  // Group entities by group_tag.
  //
  // Both sides test for their own tag. The Vault half used to be `!== 'wwg'`,
  // which swept every future group into it — `tff` (Together Forever
  // Foundation) would have been counted as Vault money here while
  // CashFlowSummary, which tests `=== 'vault'`, left it out of both. A group
  // that is neither now shows in neither, on both screens.
  const { wwgEntities, vaultEntities } = useMemo(() => {
    const eh = data.entity_health || [];
    return {
      wwgEntities: eh.filter(e => e.group_tag === 'wwg'),
      vaultEntities: eh.filter(e => e.group_tag === 'vault'),
    };
  }, [data.entity_health]);

  // Total cash position from entity health
  const totalCash = useMemo(() => {
    const eh = data.entity_health || [];
    return eh.reduce((s, e) => s + e.projected_balance, 0);
  }, [data.entity_health]);

  const wwgCash = useMemo(() => wwgEntities.reduce((s, e) => s + e.projected_balance, 0), [wwgEntities]);
  const vaultCash = useMemo(() => vaultEntities.reduce((s, e) => s + e.projected_balance, 0), [vaultEntities]);

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            Management Dashboard
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          {alertCount > 0 && (
            <Badge variant="destructive" className="text-xs px-2 py-1 gap-1">
              <CircleAlert size={12} /> {alertCount} alert{alertCount > 1 ? 's' : ''}
            </Badge>
          )}
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => nav('fin-pivot')}>
            Cash Flow <ChevronRight size={12} className="ml-0.5" />
          </Button>
        </div>
      </div>

      {/* ── Row 1: Cash Position + Month P&L ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Cash Position - wider */}
        <Card className="shadow-xs lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Projected Cash Position</span>
              <Wallet size={14} className="text-muted-foreground" />
            </div>
            <div className={`text-3xl font-bold tabular-nums ${plColor(totalCash)}`}>
              {f(totalCash, true)}
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-muted-foreground">WWG <span className="font-mono font-medium text-foreground">{f(wwgCash, true)}</span></span>
              <span className="text-muted-foreground">Tria <span className="font-mono font-medium text-foreground">{f(vaultCash, true)}</span></span>
            </div>
            {data.monthly_opening_balance > 0 && (
              <div className="mt-2 pt-2 border-t text-xs text-muted-foreground flex justify-between">
                <span>Opening: <span className="font-mono text-foreground">{f(data.monthly_opening_balance, true)}</span></span>
                <span>Net flow: <span className={`font-mono ${plColor(data.current_month_pl)}`}>{data.current_month_pl >= 0 ? '+' : ''}{f(data.current_month_pl, true)}</span></span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Month P&L */}
        <Card className="shadow-xs">
          <CardContent className="p-4">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Month P&L</div>
            <div className={`text-2xl font-bold tabular-nums ${plColor(data.current_month_pl)}`}>{f(data.current_month_pl, true)}</div>
            <div className="text-[11px] text-muted-foreground mt-1 space-y-0.5">
              <div className="flex justify-between"><span>Revenue</span><span className="font-mono text-emerald-600">{f(data.current_month_revenue, true)}</span></div>
              <div className="flex justify-between"><span>Expenses</span><span className="font-mono text-red-500">{f(Math.abs(data.current_month_expenses), true)}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Week P&L */}
        <Card className="shadow-xs">
          <CardContent className="p-4">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">This Week</div>
            <div className={`text-2xl font-bold tabular-nums ${plColor(data.this_week.net)}`}>{f(data.this_week.net, true)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {data.last_week.net !== 0 && (
                <span>
                  vs last week{' '}
                  <span className={`font-mono ${plColor(data.this_week.net - data.last_week.net)}`}>
                    {data.this_week.net - data.last_week.net >= 0 ? '+' : ''}{f(data.this_week.net - data.last_week.net, true)}
                  </span>
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* YTD */}
        <Card className="shadow-xs">
          <CardContent className="p-4">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">YTD Net</div>
            <div className={`text-2xl font-bold tabular-nums ${plColor(data.ytd_pl)}`}>{f(data.ytd_pl, true)}</div>
            <div className="text-[11px] text-muted-foreground mt-1 space-y-0.5">
              <div className="flex justify-between"><span>Revenue</span><span className="font-mono text-emerald-600">{f(data.ytd_revenue, true)}</span></div>
              <div className="flex justify-between"><span>Expenses</span><span className="font-mono text-red-500">{f(Math.abs(data.ytd_expenses), true)}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Row 2: Plan vs Actual Reconciliation (full width) ── */}
      <Card className="shadow-xs">
        <CardHeader className="pb-1 pt-4 px-5">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingDown size={14} /> Budget vs Plan vs Actual
            <span className="text-[10px] font-normal text-muted-foreground ml-2">Current month</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-40"></th>
                  <th className="text-right py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Revenue</th>
                  <th className="text-right py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Expenses</th>
                  <th className="text-right py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Net P&L</th>
                  <th className="text-right py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-24">Status</th>
                </tr>
              </thead>
              <tbody className="text-xs">
                <ReconcRow
                  label="Budget Plan"
                  revenue={data.monthly_budget_plan.revenue}
                  expenses={data.monthly_budget_plan.expenses}
                  net={data.monthly_budget_plan.net}
                />
                <ReconcRow
                  label="Corrected Plan"
                  revenue={data.monthly_corrected_plan.revenue}
                  expenses={data.monthly_corrected_plan.expenses}
                  net={data.monthly_corrected_plan.net}
                />
                <ReconcRow
                  label="Actual (Fact)"
                  revenue={data.current_month_revenue}
                  expenses={data.current_month_expenses}
                  net={data.current_month_pl}
                  bold
                />
                <tr className="border-t-2 border-border">
                  <td className="py-2 text-[10px] font-medium text-muted-foreground">Variance vs Budget</td>
                  <td className="py-2 text-right">
                    <VarianceBadge value={data.plan_vs_fact_variance.budget_revenue_var} base={data.monthly_budget_plan.revenue} />
                  </td>
                  <td className="py-2 text-right">
                    <VarianceBadge value={data.plan_vs_fact_variance.budget_expense_var} base={data.monthly_budget_plan.expenses} invertColor />
                  </td>
                  <td className="py-2 text-right">
                    <VarianceBadge value={data.plan_vs_fact_variance.budget_net_var} base={data.monthly_budget_plan.net} />
                  </td>
                  <td className="py-2 text-right">
                    <ReconcStatus netVar={data.plan_vs_fact_variance.budget_net_var} base={data.monthly_budget_plan.net} />
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-[10px] font-medium text-muted-foreground">Variance vs Corrected</td>
                  <td className="py-2 text-right">
                    <VarianceBadge value={data.plan_vs_fact_variance.corrected_revenue_var} base={data.monthly_corrected_plan.revenue} />
                  </td>
                  <td className="py-2 text-right">
                    <VarianceBadge value={data.plan_vs_fact_variance.corrected_expense_var} base={data.monthly_corrected_plan.expenses} invertColor />
                  </td>
                  <td className="py-2 text-right">
                    <VarianceBadge value={data.plan_vs_fact_variance.corrected_net_var} base={data.monthly_corrected_plan.net} />
                  </td>
                  <td className="py-2 text-right">
                    <ReconcStatus netVar={data.plan_vs_fact_variance.corrected_net_var} base={data.monthly_corrected_plan.net} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── Row 3: Entity Health + Alerts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Entity Health Table - 2/3 width */}
        <Card className="shadow-xs lg:col-span-2">
          <CardHeader className="pb-1 pt-4 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Building2 size={14} /> Entity Health
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => nav('fin-entities')}>
                All entities <ChevronRight size={12} className="ml-0.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            {(!data.entity_health || data.entity_health.length === 0) ? (
              <p className="text-xs text-muted-foreground text-center py-6">No entity data available.</p>
            ) : (
              <div className="space-y-3">
                <EntityGroup label="WWG Group" entities={wwgEntities} />
                <EntityGroup label="Tria Group" entities={vaultEntities} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts & Data Gaps - 1/3 width */}
        <Card className="shadow-xs">
          <CardHeader className="pb-1 pt-4 px-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle size={14} /> Alerts & Gaps
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="space-y-2">
              {gaps && gaps.overdue_items_count > 0 && (
                <AlertRow
                  severity="error"
                  text={`${gaps.overdue_items_count} overdue item${gaps.overdue_items_count > 1 ? 's' : ''} (${f(gaps.overdue_items_total, true)})`}
                />
              )}
              {data.entity_health?.filter(e => e.below_min_capital).map(e => (
                <AlertRow
                  key={e.entity_id}
                  severity="error"
                  text={`${e.entity_name} below min capital (${f(e.projected_balance, true)} / ${f(e.min_capital_eur, true)})`}
                />
              ))}
              {gaps && gaps.entities_missing_ob > 0 && (
                <AlertRow
                  severity="warn"
                  text={`${gaps.entities_missing_ob} / ${gaps.total_entities} entities missing opening balance`}
                />
              )}
              {gaps && gaps.stale_balance_entities > 0 && (
                <AlertRow
                  severity="warn"
                  text={`${gaps.stale_balance_entities} entities have stale balance data (>14 days)`}
                />
              )}
              {data.cashflow_items.upcoming_expenses_count > 0 && (
                <AlertRow
                  severity="info"
                  text={`${data.cashflow_items.upcoming_expenses_count} expense${data.cashflow_items.upcoming_expenses_count > 1 ? 's' : ''} due next 7 days (${f(data.cashflow_items.upcoming_expenses, true)})`}
                />
              )}
              {alertCount === 0 && !data.cashflow_items.upcoming_expenses_count && (
                <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground justify-center">
                  <CircleCheck size={14} className="text-emerald-500" />
                  No alerts — all clear
                </div>
              )}

              {/* Data completeness bar */}
              {gaps && gaps.total_entities > 0 && (
                <div className="pt-3 border-t mt-3">
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>Opening balance coverage</span>
                    <span className="font-mono">{gaps.entities_with_ob}/{gaps.total_entities}</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        gaps.entities_with_ob / gaps.total_entities > 0.8 ? 'bg-emerald-500' :
                        gaps.entities_with_ob / gaps.total_entities > 0.5 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${(gaps.entities_with_ob / gaps.total_entities) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Row 4: Weekly Trend Chart ── */}
      <Card className="shadow-xs">
        <CardHeader className="pb-1 pt-4 px-5">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 size={14} /> Weekly Trend
              <span className="text-[10px] font-normal text-muted-foreground">Last 12 weeks</span>
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => nav('fin-pivot')}>
              Details <ChevronRight size={12} className="ml-0.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          {chartData.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-10">
              No weekly cashflow data yet. Enter fact data in Cash Flow view.
            </p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" opacity={0.5} />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmtRaw(v, true)} />
                  <Tooltip
                    formatter={(value: number, name: string) => [fmtRaw(value), name]}
                    contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                  />
                  <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1} />
                  <Bar dataKey="Revenue" fill="#10B981" radius={[3, 3, 0, 0]} opacity={0.8} />
                  <Bar dataKey="Expenses" fill="#EF4444" radius={[3, 3, 0, 0]} opacity={0.8} />
                  <Line dataKey="Net" type="monotone" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Row 5: Cashflow Items Summary ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MiniStat label="Planned expenses (month)" value={data.cashflow_items.planned_expenses_this_month} color="text-amber-600" />
        <MiniStat label="Completed expenses (month)" value={data.cashflow_items.completed_expenses_this_month} color="text-emerald-600" />
        <MiniStat label="Upcoming (7 days)" value={data.cashflow_items.upcoming_expenses} color="text-red-500" count={data.cashflow_items.upcoming_expenses_count} />
        <MiniStat label="Completion rate" value={
          data.cashflow_items.planned_expenses_this_month + data.cashflow_items.completed_expenses_this_month > 0
            ? (data.cashflow_items.completed_expenses_this_month / (data.cashflow_items.planned_expenses_this_month + data.cashflow_items.completed_expenses_this_month)) * 100
            : 0
        } color="text-foreground" isPct />
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function ReconcRow({ label, revenue, expenses, net, bold }: {
  label: string; revenue: number; expenses: number; net: number; bold?: boolean;
}) {
  const { fmt } = useCurrency();
  const f = (v: number) => fmt(v, 'EUR');
  const cls = bold ? 'font-semibold bg-muted/30' : '';
  return (
    <tr className={`border-b border-border/40 ${cls}`}>
      <td className="py-2 text-xs font-medium">{label}</td>
      <td className="py-2 text-right font-mono text-xs text-emerald-600">{f(revenue)}</td>
      <td className="py-2 text-right font-mono text-xs text-red-500">{f(Math.abs(expenses))}</td>
      <td className={`py-2 text-right font-mono text-xs font-medium ${plColor(net)}`}>{f(net)}</td>
      <td></td>
    </tr>
  );
}

function VarianceBadge({ value, base, invertColor }: { value: number; base: number; invertColor?: boolean }) {
  const { fmt } = useCurrency();
  if (value === 0 && base === 0) return <span className="text-[10px] text-muted-foreground">—</span>;
  const pctStr = varPct(base + value, base);
  const isGood = invertColor ? value <= 0 : value >= 0;
  const color = value === 0 ? 'bg-muted text-muted-foreground' :
    isGood ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' :
    'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300';
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 font-mono ${color} border-0`}>
      {value > 0 ? '+' : ''}{fmt(value, 'EUR', { compact: true })} {pctStr && <span className="opacity-70 ml-0.5">{pctStr}</span>}
    </Badge>
  );
}

function ReconcStatus({ netVar, base }: { netVar: number; base: number }) {
  if (base === 0 && netVar === 0) return <span className="text-[10px] text-muted-foreground">No data</span>;
  const pct = base !== 0 ? Math.abs(netVar / Math.abs(base)) * 100 : 100;
  if (pct <= 5) return <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-0">On track</Badge>;
  if (pct <= 15) return <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border-0">Monitor</Badge>;
  return <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300 border-0">Off track</Badge>;
}

function AlertRow({ severity, text }: { severity: 'error' | 'warn' | 'info'; text: string }) {
  const icon = severity === 'error'
    ? <CircleAlert size={13} className="text-red-500 shrink-0 mt-0.5" />
    : severity === 'warn'
    ? <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
    : <Clock size={13} className="text-blue-500 shrink-0 mt-0.5" />;
  const bg = severity === 'error' ? 'bg-red-50 dark:bg-red-950/30' :
    severity === 'warn' ? 'bg-amber-50 dark:bg-amber-950/30' : 'bg-blue-50 dark:bg-blue-950/30';
  return (
    <div className={`flex items-start gap-2 px-2.5 py-2 rounded-md text-xs ${bg}`}>
      {icon}
      <span>{text}</span>
    </div>
  );
}

function EntityGroup({ label, entities }: { label: string; entities: EntityHealth[] }) {
  const { fmt } = useCurrency();
  const f = (v: number) => fmt(v, 'EUR', { compact: true });
  if (entities.length === 0) return null;
  const total = entities.reduce((s, e) => s + e.projected_balance, 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
        <span className="text-xs font-mono font-medium">{f(total)}</span>
      </div>
      <div className="space-y-0">
        {entities.map(e => (
          <div key={e.entity_id} className={`flex items-center justify-between py-1 px-2 rounded text-xs ${e.below_min_capital ? 'bg-red-50 dark:bg-red-950/30' : e.missing_ob ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <EntityStatusDot entity={e} />
              <span className="truncate">{e.entity_name}</span>
              {e.missing_ob && <Badge variant="outline" className="text-[9px] px-1 py-0 text-amber-600 border-amber-300">No OB</Badge>}
              {e.below_min_capital && <Badge variant="outline" className="text-[9px] px-1 py-0 text-red-600 border-red-300">Min cap</Badge>}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[10px] text-muted-foreground">
                <span className="text-emerald-600 font-mono">{e.month_revenue > 0 ? `+${f(e.month_revenue)}` : '-'}</span>
                {' / '}
                <span className="text-red-500 font-mono">{e.month_expenses > 0 ? `-${f(e.month_expenses)}` : '-'}</span>
              </span>
              <span className={`font-mono text-xs font-medium w-20 text-right ${plColor(e.projected_balance)}`}>
                {f(e.projected_balance)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EntityStatusDot({ entity }: { entity: EntityHealth }) {
  if (entity.below_min_capital) return <CircleAlert size={10} className="text-red-500 shrink-0" />;
  if (entity.missing_ob) return <CircleMinus size={10} className="text-amber-500 shrink-0" />;
  return <CircleCheck size={10} className="text-emerald-500 shrink-0" />;
}

function MiniStat({ label, value, color, count, isPct }: { label: string; value: number; color: string; count?: number; isPct?: boolean }) {
  const { fmt } = useCurrency();
  return (
    <Card className="shadow-xs">
      <CardContent className="p-3">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
        <div className={`text-lg font-bold tabular-nums ${color}`}>
          {isPct ? `${value.toFixed(0)}%` : fmt(value, 'EUR', { compact: true })}
          {count != null && <span className="text-xs font-normal text-muted-foreground ml-1">({count})</span>}
        </div>
      </CardContent>
    </Card>
  );
}
